/* The Presentations panel (Build): a deck, its sections, and the slides under
 * each section.
 *
 * Step 3 of docs/presentations-spec.md, her shape 2026-09-16: slides are the
 * rows, grouped under the parts of the talk. Brill PROPOSES a few slides for a
 * section in a card — "Add these slides" / "Try again" — and nothing lands until
 * she says so (rule 4). Each slide is the shared Block row with a live preview
 * drawn from the same shapes as the download.
 *
 * The Call Scripts shape otherwise: no Level 1 (spec 8.5), so Table and Deck
 * are two views of one list and keep the toggle (rule 9b); type asked on Add;
 * settings behind the cog as a pop-up (rule 8); Done is her mark on the deck.
 *
 * NOT YET, said once: sections do not reorder by drag (slides do); a slide
 * cannot be moved to another section; the
 * Weekly Teaching Deck does not yet pull a course module in.
 */
import { useEffect, useRef, useState } from "react";
import {
  DECK_STATE_LABEL, PRESENTATION_TYPES, applySlideBlock, buildProposalContext, buildSectionContext,
  buildSlideContext, deckSkin, deckSlides, deckState, deckWords, groupSlides, parseProposal,
  parseSectionSlidesBlock, parseSlideBlock, presentationLabel, presentationTypeDef, slideKind,
  slideState, slideTitle, slideWords, stripSlideBlocks, changeSlideType, wordsLeftBehind,
} from "../../utils/presentations";
import { DESIGNS, SLIDE_TYPES, resolveSkin } from "../../utils/deckLayout";
import { ROLE_WORDS } from "../../utils/brandSkin";
import { deckFileName, downloadDeck } from "../../utils/deckExport";
import { timeAgo } from "../../utils/admin";
import { buildMomentumContext } from "../../utils/momentumContext";
import { SortableItem, SortableList } from "../BrowseTable";
import { Block } from "./block";
import { Drafter } from "./drafter";
import { useRecordKeep } from "../../utils/pageChat";
import { SectionStatus } from "./fields";
import { SlidePreview } from "./slidePreview";
import { DeckPreview } from "./deckPreview";
import { rungTag } from "../../utils/offerRung";
import { B, STATUS_DOT, btnGhost, btnPrimary, input, nb, pDesc, pTitle } from "./tokens";
import { AddButton, Badge, Bread, ChatWithBrill, Menu2, Note, PageTitleField, SavedTick, StateDots, Status, useTwoPane } from "./ui";
import { BarBack, L1Table, MomentumBar, RecordCopy, RecordFooter } from "./shell";
import { ChevronRight, Download, Play, Settings, Sparkles, Trash2 } from "lucide-react";

export function DeckTypePill({ type }) {
  const def = presentationTypeDef(type);
  if (!def) {
    return <span style={{ display: "inline-block", fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 20, color: B.amber, background: B.amberQuiet, border: `1px solid ${B.amberLine}`, whiteSpace: "nowrap" }}>Set type</span>;
  }
  return <span style={{ whiteSpace: "nowrap" }}><Badge cls="neutral" text={def.label} /></span>;
}

const STATE_CLS = { done: "locked", draft: "draft", empty: "retired" };
function StateTag({ state }) {
  return <Status cls={STATE_CLS[state] || "draft"} text={DECK_STATE_LABEL[state] || "Draft"} />;
}


/* ── The shell ─────────────────────────────────────────────────────────────── */
export function PresentationsShell({ view, setView, onBackToDecks, error, onDismissError, menuOpen, setMenuOpen, onAdd, draftingBack = null, children }) {
  /* On a phone PresentationsCopy renders the way back, so the bar stands down
     — the same rule Sites, Courses and Offers follow. */
  const twoPane = useTwoPane();
  /* While she is drafting, at ANY width (2026-09-24 — until then a phone
     only): the panel's chrome gives the screen up to the chat, and
     MomentumBar stands down too. The drafting screen's one row is all that
     sits above the conversation. */
  const bare = !!draftingBack;
  return (
    <>
      {/* THE PANEL'S OWN HEAD STANDS DOWN ON A PHONE WHILE SHE DRAFTS. Her
          report, 2026-09-22: the crumb, the heading and the description were
          three of the rows that pushed the conversation down to four lines and
          the input below the fold. None of them answers a question she has
          while writing a block — the drafting screen's one row names where she
          is, and the chat card names what it is writing. */}
      {!bare && (<>
      <Bread text="Build" />
      <h2 style={pTitle}>Presentations</h2>
      <p style={pDesc}>
        Slide decks written the way you write everything else here — section by section, with Brill
        drafting a few slides at a time for you to edit. They wear the colours and fonts you locked in
        My Voice &amp; Branding, and download as a PowerPoint file.
      </p>
      </>)}
      {/* NO TABLE / DECK TOGGLE. Rule 9b: where the Table is the level ABOVE
          — and here it is, decks above slides — going to it is LEAVING the
          record, so it renders as a way BACK. A toggle that changes level is a
          different gesture wearing the same clothes, which is why the old one
          on Sites never read as the way out ("it feels like table is the first
          page"). Presentations was the last panel still doing it.
          The Table screen itself gets no back: it is the top level. */}
      <MomentumBar gap={20} drafting={!!draftingBack}
        /* One at a time, rule 9c: writing a slide, the way back goes to the
           deck, not past it to all of them. */
        lead={view !== "table" && draftingBack
          ? <BarBack {...draftingBack} />
          : view !== "table" && twoPane && onBackToDecks && <BarBack label="← All decks" onClick={onBackToDecks} />}
        add={
        <div style={{ position: "relative", display: "inline-block" }}>
          <AddButton label="Add presentation" onClick={() => setMenuOpen(!menuOpen)} />
          {menuOpen && (
            <Menu2 head="What kind of presentation?" items={PRESENTATION_TYPES.map((t) => [
              "🎤", t.label, t.hint, false, () => { setMenuOpen(false); onAdd(t.id); },
            ])} />
          )}
        </div>
      } />
      {error && (
        <div onClick={onDismissError} style={{ background: "var(--danger-quiet)", border: "1px solid var(--danger-line)", borderRadius: 9, padding: "11px 14px", marginBottom: 16, fontSize: 13, color: "var(--danger-text)", cursor: "pointer" }}>
          <b>Your presentations didn't save.</b> {error} Nothing was lost — try again once you're back online.
        </div>
      )}
      {children}
    </>
  );
}


/* ── Table ─────────────────────────────────────────────────────────────────── */
export const DECKS_COLS = "28px 2fr 1fr 1.4fr .6fr .8fr .8fr 34px";

export function PresentationsTable({ presentations = [], slidesFor, loading, onOpen, onDelete, onReorder }) {
  const ordered = [...presentations].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  return (
    <>
      <L1Table
        records={ordered} cols={DECKS_COLS}
        labels={["Presentation", "Type", "Slides", "Words", "Updated", "Status", ""]}
        loading={loading} loadingText="Loading your presentations…"
        empty={<>No presentations yet. Click <b style={nb}>Add presentation</b> and pick what kind — its sections are waiting for you.</>}
        onOpen={onOpen} onDelete={onDelete} onReorder={onReorder} deleteTitle="Delete presentation"
        rowFor={(p) => {
          const slides = slidesFor(p.id);
          const words = deckWords(slides);
          return (
            <>
              <div style={{ minWidth: 0, fontSize: 13.5, fontWeight: 600, color: p.name ? B.text : B.ghost, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{presentationLabel(p)}</div>
              <div style={{ minWidth: 0 }}><DeckTypePill type={p.type} /></div>
              <StateDots noun="slide" states={slides.map(slideState)} titles={slides.map((s) => `${slideKind(s.slideType).label} — ${slideWords(s) ? `${slideWords(s)} words` : "not written"}`)} />
              <div style={{ fontSize: 12.5, color: words ? B.mid : B.ghost, fontVariantNumeric: "tabular-nums" }}>{words || "—"}</div>
              <div style={{ fontSize: 12, color: B.faint, whiteSpace: "nowrap" }}>{p.updatedAt ? timeAgo(p.updatedAt) : "—"}</div>
              <div><StateTag state={deckState(p, slides)} /></div>
            </>
          );
        }} />
      {presentations.length > 0 && <Note><b style={nb}>Open a presentation to build its slides.</b> Drag by the grip to put them in your order.</Note>}
    </>
  );
}


/* ── Deck view: the rail ───────────────────────────────────────────────────── */
export function PresentationsCopy({ sections = [], slides = [], selectedSlideId, onSelectSlide, drafting, onBackToDecks, children }) {
  /* THE RAIL IS THE DECK'S SLIDES, not the list of decks (her report,
     2026-09-17: "in presentations the deck panel should have the different
     pages in the deck not the different presentations").

     Every other panel's rail holds the CHILDREN of the record she has open —
     a site's pages, a course's modules — and this one held its siblings, so
     the one list she needed while building a deck was the one list missing.
     Switching decks is going UP a level, which is the Table (rule 9b).

     No Done on a slide and no drag here: a slide is L3, which carries no mark
     (rule 6), and slides reorder inside their own section, by the grip on the
     row itself. */
  const bySection = groupSlides(sections, slides);
  const rows = bySection.flatMap((g) => g.slides.map((s) => ({ ...s, sectionName: g.section?.name || "Loose slides" })));
  return (
    <RecordCopy bare={drafting} records={rows} selectedId={selectedSlideId} onSelect={onSelectSlide}
      onExit={onBackToDecks} exitLabel="All decks"
      noun="slide" emptyHint={<>No slides yet. Add a section below, then <b style={nb}>Draft more slides</b> or add one yourself.</>}
      rowFor={(s) => {
        const state = slideState(s);
        return {
          title: slideTitle(s),
          meta: [s.sectionName, slideKind(s.slideType).label].filter(Boolean).join(" · "),
          dot: B[STATUS_DOT[state]], dotTitle: state === "draft" ? "Written" : "Not written yet",
          markable: false,
        };
      }}>
      {children}
    </RecordCopy>
  );
}


/* ── One presentation ──────────────────────────────────────────────────────── */
export function PresentationDeck({
  deck, sections = [], slides = [], offers = [], voiceOutputs = {}, brand = null, error,
  onSave, onDelete, onSaveSection, onAddSection, onDeleteSection,
  onSaveSlide, onAddSlides, onRemoveSlide, onReorderSlides,
  onDraftSlide, onDraftSection, proposals = {}, onPropose, onAcceptProposal, onDismissProposal,
  onSparkleSlide, sparklingSlideId = null, draftedSlide = null, pickedSlideId = null,
}) {
  const [name, setName] = useState(deck?.name || "");
  const [cog, setCog] = useState(() => !presentationTypeDef(deck?.type));
  const [exporting, setExporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  if (!deck) return null;

  const state = deckState(deck, slides);
  const words = deckWords(slides);
  const { skin: rawSkin, read } = deckSkin(deck, voiceOutputs, brand);
  const skin = resolveSkin(rawSkin);
  const missingColours = skin.missing.filter((r) => !r.startsWith("font") && !r.startsWith("text"));
  const set = (k, v) => onSave({ ...deck, [k]: v });
  const groups = groupSlides(sections, slides);

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 10 }}>
        <button onClick={() => setCog(true)} title="Change what kind of presentation this is" style={{ padding: 0, border: "none", background: "none", cursor: "pointer" }}>
          <DeckTypePill type={deck.type} />
        </button>
        {(() => {
          const rung = rungTag(deck, offers, "asset");
          return rung ? <Badge cls={rung.badge} text={rung.label} /> : null;
        })()}
        {state === "empty" ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600, color: B.mid }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: B[STATUS_DOT[state]] }} />
            {DECK_STATE_LABEL[state]}
          </span>
        ) : (
          <SectionStatus value={deck.status === "done" ? "done" : "draft"} onChange={(status) => set("status", status)} />
        )}
        <span style={{ fontSize: 11.5, color: B.faint }}>{slides.length} slide{slides.length === 1 ? "" : "s"} · {words ? `${words} words` : "no copy yet"}</span>
        <SavedTick problem={error ? "That didn't save — try again" : ""} />
        {/* Preview is the TEAL one: it is the action she reaches for most, and
            looking costs nothing. Download is what she does once, at the end. */}
        <button onClick={() => setPreviewing(true)} disabled={!slides.length}
          style={{ ...btnGhost, marginLeft: "auto", padding: "7px 13px", fontSize: 12, display: "flex", alignItems: "center", gap: 6, color: B.teal, borderColor: B.tealLine, opacity: slides.length ? 1 : 0.5 }}>
          <Play size={13} /> Preview deck
        </button>
        <button onClick={() => setExporting(true)} disabled={!slides.length}
          style={{ ...btnGhost, padding: "7px 13px", fontSize: 12, display: "flex", alignItems: "center", gap: 6, opacity: slides.length ? 1 : 0.5 }}>
          <Download size={13} /> Download .pptx
        </button>
        <button onClick={() => setCog(!cog)} title="Presentation settings" aria-label="Presentation settings" aria-expanded={cog}
          style={{ ...btnGhost, padding: "7px 12px", fontSize: 12, display: "flex", alignItems: "center", gap: 6, color: cog ? B.teal : B.mid, borderColor: cog ? B.tealLine : B.border2 }}>
          <Settings size={13} /> Settings
        </button>
      </div>

      <div style={{ display: "flex", marginBottom: 12 }}>
        <PageTitleField value={name} onChange={setName} placeholder="Name this presentation" label="Presentation name"
          onCommit={() => { if (name !== (deck.name || "")) set("name", name); }} />
      </div>

      {/* The skin gap, said where she will see it — a deck in the wrong
          colours looks finished (the silent failure step 2 was built against). */}
      {missingColours.length > 0 && (
        <button onClick={() => setCog(true)}
          style={{ display: "block", width: "100%", textAlign: "left", fontFamily: "inherit", cursor: "pointer", fontSize: 12.5, lineHeight: 1.55, color: B.text, background: B.amberQuiet, border: `1px solid ${B.amberLine}`, borderRadius: 9, padding: "9px 13px", marginBottom: 14 }}>
          <b style={{ color: B.amber }}>Your colors aren't all set.</b> Not found in Me → Brand or My Voice &amp; Branding: {missingColours.map((r) => ROLE_WORDS[r]).join(", ")}. The deck is using stand-ins — set them in Me → Brand.
        </button>
      )}

      {cog && (
        <DeckSettings deck={deck} offers={offers} read={read}
          onSave={(patch) => { onSave({ ...deck, ...patch }); setCog(false); }}
          onClose={() => setCog(false)} />
      )}
      {previewing && (
        <DeckPreview title={presentationLabel(deck)} slides={deckSlides(sections, slides)} skin={skin} design={deck.design}
          onClose={() => setPreviewing(false)} />
      )}
      {exporting && (
        <ExportDialog deck={deck} onClose={() => setExporting(false)}
          onDownload={() => downloadDeck({ title: presentationLabel(deck), author: deck.presenter || "", slides: deckSlides(sections, slides), skin: rawSkin, design: deck.design || "editorial" })} />
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
        {groups.map((g, gi) => (
          <DeckSection key={g.section?.id || "loose"} index={gi} section={g.section} slides={g.slides} skin={skin} design={deck.design}
            /* A DECK WITH ONE SECTION OPENS; a deck with several starts folded
               (her read, 2026-09-17: "presentation sections need to be
               collapsable, it looks messy right now"). A lone section has
               nothing to tidy away, and folding it would mean a click to see
               the only thing on the screen. */
            defaultOpen={groups.length <= 1}
            proposal={g.section ? proposals[g.section.id] : null}
            usedAs={{ type: "deck", id: deck.id, label: presentationLabel(deck) }}
            onSparkleSlide={onSparkleSlide} sparklingSlideId={sparklingSlideId} draftedSlide={draftedSlide}
            pickedSlideId={pickedSlideId}
            onSaveSection={onSaveSection} onDeleteSection={onDeleteSection}
            onSaveSlide={onSaveSlide} onRemoveSlide={onRemoveSlide}
            onAddSlide={(type) => onAddSlides?.(g.section, [{ slideType: type }])}
            onReorder={(from, to) => onReorderSlides?.(g.section, from, to)}
            onDraftSlide={onDraftSlide}
            onDraftSection={g.slides.length ? () => onDraftSection?.(g.section, g.slides[0]) : null}
            onPropose={() => onPropose?.(g.section)}
            onAccept={() => onAcceptProposal?.(g.section)}
            onDismiss={() => onDismissProposal?.(g.section)} />
        ))}
      </div>
      <div style={{ marginTop: 14 }}>
        <button onClick={() => onAddSection?.(deck)} style={{ ...btnGhost, padding: "9px 15px", fontSize: 12.5 }}>+ Add section</button>
      </div>

      <RecordFooter tick={false} onDelete={() => onDelete(deck)} />
    </>
  );
}


/* ── One section: its heading, Brill's proposal, its slides ────────────────── */
function DeckSection({
  index, section, slides, skin, design, proposal, usedAs = null, onSparkleSlide, sparklingSlideId = null, draftedSlide = null, pickedSlideId = null,
  onSaveSection, onDeleteSection, onSaveSlide, onRemoveSlide, onAddSlide, onReorder,
  onDraftSlide, onDraftSection, onPropose, onAccept, onDismiss, defaultOpen = true,
}) {
  const [nameDraft, setNameDraft] = useState(section?.name || "");
  const [briefDraft, setBriefDraft] = useState(section?.brief || "");
  const [menu, setMenu] = useState(false);
  const [open, setOpen] = useState(defaultOpen);
  const busy = !!proposal?.busy;

  /* THINGS THAT OPEN A SECTION WHETHER OR NOT SHE DID. Each of them puts
     something inside it that she is meant to be looking at, and a folded
     section would hide it without a word:
       · the slide Brill just wrote, or the one she picked in the rail — the
         same two conditions that open the slide's own drawer;
       · a proposal waiting on her yes (rule 4: Brill proposes, she locks — a
         card she cannot see is not a proposal);
       · Brill still drafting, or an error from the last attempt.
     It only ever OPENS. Folding it again is hers, or a section would spring
     shut under her the moment a draft finished. */
  const holds = (id) => !!id && slides.some((s) => s.id === id);
  const mustOpen = busy || !!proposal?.error || !!proposal?.slides?.length
    || holds(draftedSlide?.slideId) || holds(pickedSlideId);
  useEffect(() => { if (mustOpen) setOpen(true); }, [mustOpen]);

  const words = slides.reduce((n, s) => n + slideWords(s), 0);
  const shown = open || mustOpen;

  return (
    /* A WHITE CARD, like the rail beside it (her read, 2026-09-17: "what does it
       look like to put the sections in white background like the sidebar" —
       after "it feels messy"). Folded, the deck reads as a stack of cards with
       one line of state each, which is the same shape as every list in the app.
       The SLIDES inside stay sunken until they are opened (block.jsx), so a card
       holding rows still has two tones in it rather than going flat. */
    <div style={{
      background: B.surface, border: `1px solid ${B.border}`, borderRadius: 12,
      padding: "13px 15px",
    }}>
      {section ? (
        /* The rule under the heading is a DIVIDER between it and the slides, so
           it only exists when there are slides under it to divide from. */
        <div style={{ borderBottom: shown ? `1px solid ${B.border}` : "none", paddingBottom: shown ? 8 : 0, marginBottom: shown ? 10 : 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {/* THE FOLD. Not the whole strip, which is the rule everywhere else
                (2) — because this strip IS the name field and the brief, and a
                click meant for the text would close the section under her. So
                the target is the number and the chevron, which are the parts
                that are not typeable. */}
            <button onClick={() => setOpen(!shown)} aria-expanded={shown}
              aria-label={`${shown ? "Collapse" : "Expand"} section ${section.name || index + 1}`}
              style={{ display: "flex", alignItems: "center", gap: 7, padding: "2px 4px 2px 0", border: "none", background: "none", cursor: "pointer", fontFamily: "inherit" }}>
              <ChevronRight size={13} style={{ color: B.faint, transform: shown ? "rotate(90deg)" : "none", transition: "transform 120ms" }} />
              <span style={{ fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700 }}>Section {index + 1}</span>
            </button>
            <input value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} aria-label="Section name" placeholder="Name this section"
              onBlur={() => { if (nameDraft !== (section.name || "")) onSaveSection({ ...section, name: nameDraft }); }}
              style={{ ...input, width: "auto", flex: "1 1 180px", padding: "5px 8px", fontSize: 15, fontWeight: 700, border: "1px solid transparent", background: "transparent" }} />
            <button onClick={onPropose} disabled={busy} title="Brill suggests a few slides for this section"
              style={{ ...btnGhost, padding: "6px 11px", fontSize: 12, display: "flex", alignItems: "center", gap: 6, color: B.teal, borderColor: B.tealLine, opacity: busy ? 0.6 : 1 }}>
              <Sparkles size={13} /> {busy ? "Brill is drafting…" : slides.length ? "Draft more slides" : "Draft slides"}
            </button>
            {onDraftSection && (
              <ChatWithBrill label="Write this section with Brill" onClick={onDraftSection} size={14} />
            )}
            <button onClick={() => onDeleteSection?.(section, slides)} title="Delete section" aria-label={`Delete section ${section.name || index + 1}`}
              style={{ background: "none", border: "none", cursor: "pointer", color: B.faint, padding: 4, display: "flex" }}><Trash2 size={14} /></button>
          </div>
          {shown ? (
            <input value={briefDraft} onChange={(e) => setBriefDraft(e.target.value)} aria-label="What this section does" placeholder="What this part of the talk does — Brill drafts to this"
              onBlur={() => { if (briefDraft !== (section.brief || "")) onSaveSection({ ...section, brief: briefDraft }); }}
              style={{ ...input, marginTop: 4, padding: "4px 8px", fontSize: 12.5, color: B.dim, border: "1px solid transparent", background: "transparent" }} />
          ) : (
            /* FOLDED, IT STILL HAS TO SAY HOW FAR ALONG IT IS. A row of
               headings with nothing under them tells her a section exists and
               nothing else — so the fold keeps the dots and the count, the same
               pair the landing table shows. */
            <button onClick={() => setOpen(true)} aria-label={`Expand section ${section.name || index + 1}`}
              style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 6, padding: 0, border: "none", background: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
              <StateDots noun="slide" states={slides.map(slideState)}
                titles={slides.map((s) => `${slideKind(s.slideType).label} — ${slideWords(s) ? `${slideWords(s)} words` : "not written"}`)} />
              <span style={{ fontSize: 11.5, color: B.faint }}>{words ? `${words} words` : "Nothing written yet"}</span>
              {section.brief && (
                <span style={{ fontSize: 11.5, color: B.dim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 320 }}>{section.brief}</span>
              )}
            </button>
          )}
        </div>
      ) : (
        <div style={{ fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginBottom: 10 }}>Unsorted slides</div>
      )}

      {shown && (
        <>
      {proposal?.error && (
        <div style={{ fontSize: 12.5, color: B.amber, marginBottom: 8 }}>{proposal.error}</div>
      )}
      {proposal?.slides?.length > 0 && (
        <Proposal slides={proposal.slides} skin={skin} design={design} note={proposal.note} busy={busy}
          onAccept={onAccept} onRetry={onPropose} onDismiss={onDismiss} />
      )}

      {slides.length === 0 && !proposal?.slides?.length ? (
        <div style={{ border: `1px dashed ${B.border2}`, borderRadius: 10, padding: 18, textAlign: "center", color: B.dim, fontSize: 12.5, lineHeight: 1.6 }}>
          No slides here yet. <b style={nb}>Draft slides</b> and Brill suggests a few, or add one yourself.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <SortableList ids={slides.map((s) => s.id)} onReorder={onReorder}>
            {slides.map((s) => (
              <SortableItem key={s.id} id={s.id}>
                {({ handle, dragging }) => (
                  <div id={`slide-${s.id}`}>
                  <SlideBlock
                    /* Keyed by the draft stamp, like every other row: a Block
                       holds its own content from the moment it mounts. Picking
                       the slide in the rail is the other thing that opens it,
                       so it keys on that too. */
                    key={`${s.id}:${draftedSlide?.slideId === s.id ? draftedSlide.n : 0}:${pickedSlideId === s.id ? "picked" : ""}`}
                    slide={s} skin={skin} design={design} onSave={onSaveSlide} onRemove={onRemoveSlide}
                    startOpen={draftedSlide?.slideId === s.id || pickedSlideId === s.id}
                    usedAs={usedAs}
                    sparkle={onSparkleSlide ? {
                      busy: sparklingSlideId === s.id,
                      onClick: () => onSparkleSlide(section, s),
                      label: slideWords(s) ? "Rewrite with Brill" : "Write with Brill",
                    } : null}
                    onChat={onDraftSlide ? () => onDraftSlide(section, s) : null} dragHandle={handle} dragging={dragging} />
                  </div>
                )}
              </SortableItem>
            ))}
          </SortableList>
        </div>
      )}
      {section && (
        <div style={{ position: "relative", display: "inline-block", marginTop: 8 }}>
          <button onClick={() => setMenu(!menu)} style={{ ...btnGhost, padding: "6px 12px", fontSize: 12 }}>+ Add slide</button>
          {menu && (
            <Menu2 head="What kind of slide?" items={SLIDE_TYPES.map((t) => [
              "▭", t.label, SLIDE_HINTS[t.id], false, () => { setMenu(false); onAddSlide(t.id); },
            ])} />
          )}
        </div>
      )}
        </>
      )}
    </div>
  );
}

const SLIDE_HINTS = {
  title: "The deck's name, the promise, and you",
  divider: "Opens a new part of the talk — number and title only",
  statement: "One idea, very large, nothing else",
  list: "Three to five steps, pillars or principles",
  image_text: "A picture on the left, a heading and a few lines beside it",
  text_image: "A heading and a few lines, the picture on the right",
  image_full: "One picture fills the slide, a few words across the bottom",
  gallery: "Two or three pictures side by side, each with a caption",
  quote: "A client's words — with their photo if you have one",
  stat: "One number that needs to land",
  old_new: "Before and after, side by side",
  closing: "The ask, with a button",
};


/* Brill's suggested slides, waiting on her word (rule 4). Previews, so what
   she is offered looks like what she will get. */
function Proposal({ slides, skin, design, note, busy, onAccept, onRetry, onDismiss }) {
  return (
    <div style={{ border: `1px dashed ${B.tealLine}`, background: B.tealQuiet, borderRadius: 12, padding: "12px 14px", marginBottom: 10 }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: B.teal, marginBottom: 6 }}>
        Suggested slides
      </div>
      {note && <div style={{ fontSize: 12.5, color: B.text, lineHeight: 1.55, marginBottom: 10, whiteSpace: "pre-wrap" }}>{note}</div>}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {slides.map((s, i) => (
          <div key={i} style={{ width: 200 }}>
            <SlidePreview slide={{ type: s.slideType, content: s.content }} skin={skin} design={design} width={200} label={`Suggested slide ${i + 1}`} />
            <div style={{ fontSize: 11, color: B.dim, marginTop: 4 }}>{i + 1}. {slideKind(s.slideType).label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        <button onClick={onAccept} disabled={busy}
          style={{ padding: "6px 13px", borderRadius: 7, border: "none", background: "var(--btn-bg)", color: "var(--btn-text)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          Add {slides.length === 1 ? "this slide" : `these ${slides.length} slides`}
        </button>
        <button onClick={onRetry} disabled={busy}
          style={{ padding: "6px 13px", borderRadius: 7, border: `1px solid ${B.border2}`, background: B.surface, color: B.mid, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          {busy ? "Drafting…" : "Try again"}
        </button>
        <button onClick={onDismiss}
          style={{ padding: "6px 13px", borderRadius: 7, border: "none", background: "none", color: B.faint, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
          Not now
        </button>
      </div>
    </div>
  );
}


/* One slide — the shared Block, with its preview on the row and a large one
   while she edits. The bin removes the slide; the panel asks first. */
export function SlideBlock({ slide, skin, design, onSave, onRemove, onChat, sparkle = null, startOpen = false, dragHandle, dragging, usedAs = null }) {
  // "List / Framework · The Reset Framework" — the type alone until it has words.
  const typeLabel = slideKind(slide?.slideType).label;
  const title = slideTitle(slide);
  const kind = { ...slideKind(slide?.slideType), label: title === typeLabel ? typeLabel : `${typeLabel} · ${title}` };
  const preview = (c, width) => <SlidePreview slide={{ type: slide.slideType, content: c }} skin={skin} design={design} width={width} label={`${slideKind(slide.slideType).label} preview`} />;
  /* The type the slide had when she opened it, so CANCEL undoes a type change
     along with the words — Block's own Cancel knows only the words. Taken in
     `snapshot`, which Block calls once, as she opens the editor. */
  const typeAtEdit = useRef(slide?.slideType);
  return (
    <Block
      kind={kind} noun="slide"
      content={slide?.content || {}}
      words={(c) => slideWords({ ...slide, content: c })}
      onCommit={(c) => onSave({ ...slide, content: c })}
      snapshot={() => { typeAtEdit.current = slide?.slideType; return slide?.content || {}; }}
      onCancel={(before) => {
        const type = typeAtEdit.current || slide.slideType;
        if (before && (type !== slide.slideType || JSON.stringify(before) !== JSON.stringify(slide.content || {}))) {
          onSave({ ...slide, slideType: type, content: before });
        }
        return before;
      }}
      onChat={onChat || undefined}
      /* The one-press write, as on every other row (her read, 2026-09-17: the
         row is shared, so the button should be too). The bubble opens the chat;
         this writes the slide and its notes in place. */
      sparkle={sparkle || undefined}
      /* Open on what Brill just wrote — see the note in callScripts.jsx. The
         big preview sits at the top of the open row, so she sees the slide. */
      startOpen={startOpen}
      onClear={() => onRemove?.(slide)}
      clearLabel="Remove slide"
      dragHandle={dragHandle} dragging={dragging}
      fieldProps={{ usedAs }}
      badges={(c) => preview(c, 72)}
      editTop={(c, change) => (
        <div style={{ marginBottom: 14 }}>
          <SlideTypePicker slide={slide} content={c} onChange={(to, next) => {
            change(next);
            onSave({ ...slide, slideType: to, content: next });
          }} />
          {preview(c, 360)}
        </div>
      )}
    />
  );
}

/* CHANGE SLIDE TYPE — her ask, 2026-09-23: a slide added as the wrong kind
   should not have to be deleted and written again. Above the big preview, so
   she watches the slide change shape. Her words come along (changeSlideType);
   anything the new type has no place for is KEPT, and this says so, because a
   list vanishing from the screen reads as a list deleted. */
function SlideTypePicker({ slide, content, onChange }) {
  const [left, setLeft] = useState(null);
  const from = slide?.slideType;
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: B.dim }}>
        Slide type
        <select value={from} aria-label="Slide type"
          onChange={(e) => {
            const to = e.target.value;
            if (to === from) return;
            const gone = wordsLeftBehind(content, from, to);
            setLeft(gone.length ? { labels: gone, type: slideKind(to).label, back: slideKind(from).label } : null);
            onChange(to, changeSlideType(content, from, to));
          }}
          style={{ ...input, width: "auto", padding: "5px 8px", fontSize: 12.5 }}>
          {SLIDE_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </label>
      {left && (
        <div style={{ fontSize: 12, color: B.amber, marginTop: 6, lineHeight: 1.5 }}>
          {left.type} has no place for {left.labels.join(", ")}. Nothing is deleted — change it back to {left.back} and {left.labels.length === 1 ? "it returns" : "they return"}.
        </div>
      )}
    </div>
  );
}


/* ── Settings, as a pop-up (rule 8) ───────────────────────────────────────────
   Type, presenter, offer, design — and her colours and fonts. Each colour shows
   what My Voice & Branding gave, and saving stores only what she CHANGED, so a
   later re-lock there still reaches every deck she did not override. */
const SKIN_EDIT = [
  ["bgPrimary", "Light background"], ["bgDark", "Dark background"],
  ["brandPrimary", "Brand color"], ["brandAccent", "Accent / button"],
];

export function DeckSettings({ deck, offers = [], read, onSave, onClose }) {
  const [draft, setDraft] = useState({
    type: deck?.type || null, presenter: deck?.presenter || "", offerId: deck?.offerId || null,
    design: deck?.design || "editorial", skin: { ...(deck?.skin || {}) },
  });
  const put = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const putSkin = (k, v) => setDraft((d) => ({ ...d, skin: { ...d.skin, [k]: v } }));
  const def = presentationTypeDef(draft.type);
  const fromVoice = read?.skin || {};
  const live = offers.filter((o) => o.status !== "retired");

  return (
    <div onClick={onClose} onKeyDown={(e) => { if (e.key === "Escape") onClose?.(); }}
      style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-label="Presentation settings" onClick={(e) => e.stopPropagation()}
        style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: 14, padding: "20px 20px 16px", width: "100%", maxWidth: 560, maxHeight: "calc(100vh - 32px)", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", boxSizing: "border-box" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: B.text, marginBottom: 16 }}>{presentationLabel(deck)}</div>
        <Setting label="What kind of presentation" hint={def ? def.hint : "Pick one."}>
          <select autoFocus value={draft.type || ""} onChange={(e) => put("type", e.target.value || null)} aria-label="Presentation type" style={input}>
            {!draft.type && <option value="">Choose one…</option>}
            {PRESENTATION_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </Setting>
        <Setting label="Presenter" hint="The name on the title slide.">
          <input value={draft.presenter} onChange={(e) => put("presenter", e.target.value)} aria-label="Presenter" style={input} />
        </Setting>
        <Setting label="Leads to" hint="The offer this presentation walks toward. It lists this deck under Supported by.">
          <select value={draft.offerId || ""} onChange={(e) => put("offerId", e.target.value || null)} aria-label="Connected offer" style={input}>
            <option value="">No offer</option>
            {live.map((o) => <option key={o.id} value={o.id}>{o.name || "Untitled offer"}</option>)}
          </select>
        </Setting>
        <Setting label="Design" hint="More designs are coming.">
          <select value={draft.design} onChange={(e) => put("design", e.target.value)} aria-label="Design" style={input}>
            {DESIGNS.map((d) => <option key={d.id} value={d.id}>{d.label} — {d.hint}</option>)}
          </select>
        </Setting>
        <Setting label="Colors" hint="Blank uses Me → Brand (or My Voice & Branding). Type a color code like #27233A to change one for this deck only.">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 8 }}>
            {SKIN_EDIT.map(([k, label]) => {
              const own = draft.skin[k] || "";
              const shown = own || fromVoice[k] || "";
              return (
                <label key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: B.mid }}>
                  <span style={{ width: 22, height: 22, borderRadius: 5, flexShrink: 0, border: `1px solid ${B.border2}`, background: /^#?[0-9a-f]{6}$/i.test(shown) ? (shown.startsWith("#") ? shown : `#${shown}`) : "transparent" }} />
                  <span style={{ width: 96, flexShrink: 0 }}>{label}</span>
                  <input value={own} onChange={(e) => putSkin(k, e.target.value.trim())} aria-label={label}
                    placeholder={fromVoice[k] || "not found — set one"}
                    style={{ ...input, padding: "5px 8px", fontSize: 12, borderColor: !shown ? B.amberLine : undefined }} />
                </label>
              );
            })}
          </div>
        </Setting>
        <Setting label="Fonts" hint="Blank uses My Voice & Branding. A PowerPoint file shows a font only where it is installed.">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {[["fontHeading", "Headings"], ["fontBody", "Body"]].map(([k, label]) => (
              <input key={k} value={draft.skin[k] || ""} onChange={(e) => putSkin(k, e.target.value)} aria-label={`${label} font`}
                placeholder={fromVoice[k] ? `${label}: ${fromVoice[k]}` : `${label}: not found`} style={{ ...input, padding: "6px 8px", fontSize: 12 }} />
            ))}
          </div>
        </Setting>
        {read?.notes?.length > 0 && (
          <div style={{ fontSize: 11.5, color: B.dim, lineHeight: 1.55, marginBottom: 14 }}>
            <b style={nb}>From My Voice &amp; Branding:</b> {read.notes.join(" ")}
          </div>
        )}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
          <button onClick={onClose} style={{ ...btnGhost, padding: "9px 16px", fontSize: 13 }}>Cancel</button>
          <button onClick={() => onSave?.({ ...draft, skin: Object.fromEntries(Object.entries(draft.skin).filter(([, v]) => String(v || "").trim())) })} disabled={!def}
            style={{ ...btnPrimary, padding: "9px 18px", fontSize: 13, opacity: def ? 1 : 0.5, cursor: def ? "pointer" : "not-allowed" }}>Save</button>
        </div>
      </div>
    </div>
  );
}

function Setting({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginBottom: 6 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11.5, color: B.dim, marginTop: 6, lineHeight: 1.5 }}>{hint}</div>}
    </div>
  );
}


/* ── Export, with the fidelity warning (the spec: a one-time modal) ─────────── */
export const EXPORT_WARNING = "This file is built for PowerPoint. If you open it in Google Slides, fonts and spacing may shift slightly — check it over before presenting.";

export function ExportDialog({ deck, onDownload, onClose }) {
  const [state, setState] = useState(null);
  const go = async () => {
    setState("busy");
    try { await onDownload(); onClose(); } catch (e) { setState(e?.message || "The file couldn't be made just then. Nothing was changed — try again."); }
  };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-label="Export presentation" onClick={(e) => e.stopPropagation()}
        style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: 14, padding: 20, width: "100%", maxWidth: 440, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", boxSizing: "border-box" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: B.text, marginBottom: 10 }}>Export {presentationLabel(deck)}</div>
        <p style={{ fontSize: 13, color: B.mid, lineHeight: 1.6, margin: "0 0 8px" }}>{EXPORT_WARNING}</p>
        <p style={{ fontSize: 12, color: B.dim, lineHeight: 1.55, margin: "0 0 16px" }}>
          An image slide with no picture chosen carries a placeholder — right-click it and choose Change Picture. Your speaker notes are on every slide. Saves as {deckFileName(presentationLabel(deck))}.
        </p>
        {state && state !== "busy" && <div style={{ fontSize: 12.5, color: B.amber, marginBottom: 12 }}>{state}</div>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ ...btnGhost, padding: "9px 16px", fontSize: 13 }}>Cancel</button>
          <button onClick={go} disabled={state === "busy"} style={{ ...btnPrimary, padding: "9px 18px", fontSize: 13 }}>
            {state === "busy" ? "Making the file…" : "Download .pptx"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ── Writing with Brill ────────────────────────────────────────────────────────
   COST: one API call per message or per "Draft slides" press — NEW SPEND. Her
   client, offer and voice ride along (CONTEXT_SCOPES.presentation_copy).
   Measured sibling chats run about $0.02 a message. */
export async function deckDraftReply({ token, chatContext = {}, context, messages, maxTokens = 2000 }) {
  const ctx = buildMomentumContext({
    moduleId: "presentation_copy",
    acceleratorOutputs: chatContext.acceleratorOutputs,
    activeNiche: chatContext.activeNiche,
    isStale: (id, name) => {
      const stamp = chatContext.outputStamps?.[id]?.[name];
      return !!(chatContext.activeNiche && stamp && stamp !== chatContext.activeNiche.id);
    },
  });
  const res = await fetch("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({
      moduleId: "presentation_copy",
      max_tokens: maxTokens,
      moduleLabel: "Presentation",
      archetype: chatContext.userSettings?.archetype || null,
      archetypePair: chatContext.userSettings?.archetypePair || null,
      messages: [
        ...(ctx ? [{ role: "user", content: ctx }] : []),
        { role: "user", content: context },
        ...messages,
      ],
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || "Something went wrong reaching Brill — try again in a moment. Nothing in this presentation was touched.");
  return data.content?.find((b) => b.type === "text")?.text || "";
}

/** "Draft slides" for a section: one call, no conversation. Returns
 *  { slides, note } or throws a sentence she can read. */
export async function proposeSlides({ token, deck, section, sections, slides, offers, chatContext }) {
  const reply = await deckDraftReply({
    token, chatContext, maxTokens: 3000,
    context: buildProposalContext({ presentation: deck, section, sections, slides, offers }),
    messages: [{ role: "user", content: `Draft the slides for "${section?.name || "this section"}".` }],
  });
  const proposed = parseProposal(reply);
  if (!proposed) throw new Error(stripSlideBlocks(reply) || "Brill didn't suggest any slides that time — try again.");
  return { slides: proposed, note: stripSlideBlocks(reply) };
}

/* Named once: the bar renders it (`draftingBack` on PresentationsShell) and
   Next's last step reads its words off the same string. */
export const DECK_BACK = "← Back to the deck";


export function PresentationDrafter({ deck, section, slide, sections = [], slides = [], offers = [], chatContext = {}, onSave, onBack, onGoToSlide, startScope = "block" }) {
  /* THE CHAT IS KEPT, per deck (supabase/0049): a slide's under its id, and the
     "whole" chat under its SECTION, because here the whole-record scope is one
     section of the talk. One row for the deck, so a slide moving between
     sections keeps its chat. */
  const keep = useRecordKeep({
    tool: "presentation", recordId: deck?.id, blockKey: slide?.id, recordKey: `section:${section?.id || "none"}`,
    liveKeys: [...slides.map((s) => s.id), ...sections.map((s) => `section:${s.id}`), `section:${section?.id || "none"}`],
  });
  if (!deck || !slide) return null;
  const kind = slideKind(slide.slideType);
  const sectionSlides = groupSlides(sections, slides).find((g) => g.section?.id === section?.id)?.slides || [slide];
  const index = sectionSlides.findIndex((s) => s.id === slide.id);
  const has = (mod, name) => !!chatContext.acceleratorOutputs?.[mod]?.[name];
  const contextItems = [
    { label: "Your voice", on: has("voice", "Voice Profile") },
    { label: "Your client", on: has("client", "Ideal Client Profile") },
    { label: "Your offer", on: has("offer", "Your Signature Offer") || !!offers.find((o) => o.id === deck.offerId) },
    { label: "The rest of the deck", on: slides.some((s) => s.id !== slide.id && slideWords(s) > 0) },
  ];
  return (
    <Drafter
      /* Recording the voice (supabase/0030): where a Lock lands. */
      voice={{ tool: "presentation_copy", recordType: "presentation_slide", blockId: slide?.id }}
      keep={keep}
      libraryKinds={["story"]}
      fieldProps={{ usedAs: { type: "deck", id: deck?.id, label: presentationLabel(deck) } }}
      kind={kind}
      content={slide.content || {}}
      onCommit={(content) => onSave({ ...slide, content })}
      ask={({ token, messages, content }) => deckDraftReply({
        token, chatContext, messages,
        context: buildSlideContext({ presentation: deck, section, slide: { ...slide, content }, sections, slides, offers }),
      })}
      parse={(reply, content) => {
        const block = parseSlideBlock(reply, slide);
        return block ? applySlideBlock(content, block, slide) : null;
      }}
      strip={stripSlideBlocks}
      title={`Write ${kind.label.toLowerCase()}`}
      opener={`I'm on a ${kind.label.toLowerCase()} slide in "${section?.name || "this section"}" of ${presentationLabel(deck)}. Tell me what it needs to land — or say "draft it" and I'll write the slide and its speaker notes.`}
      suggestions={{ first: ["Draft it", "Make the notes sound like me talking"], later: ["Shorter", "Punchier", "Make it sound more like me"] }}
      contextItems={contextItems}
      pager={onGoToSlide && index >= 0 ? {
        items: sectionSlides.map((s) => ({ id: s.id, label: `${slideKind(s.slideType).label}`, written: slideWords(s) > 0 })),
        index, onGo: onGoToSlide, noun: "Slide",
      } : null}
      chromeInBar
      backLabel={DECK_BACK} onBack={onBack}
      startScope={startScope}
      record={{
        blockLabel: "Slide", label: "Section", noun: "section",
        title: `Write ${section?.name || "this section"}`,
        heading: section?.name || "This section",
        opener: `We're on "${section?.name || "this section"}" — ${sectionSlides.length} slide${sectionSlides.length === 1 ? "" : "s"}. Tell me what this part of the talk has to do, or say "draft them all" and I'll write every slide and its notes.`,
        suggestions: { first: ["Draft them all", "Make them flow"], later: ["Shorter", "Punchier", "Make it sound more like me"] },
        blocks: sectionSlides.map((s) => ({ id: s.id, kind: slideKind(s.slideType), content: s.content || {} })),
        ask: ({ token, messages, contents }) => deckDraftReply({
          token, chatContext, messages, maxTokens: 3000,
          context: buildSectionContext({ presentation: deck, section, sections, offers, slides: slides.map((s) => (contents[s.id] ? { ...s, content: contents[s.id] } : s)) }),
        }),
        parse: (reply, contents) => {
          const by = parseSectionSlidesBlock(reply, sectionSlides);
          if (!by) return null;
          const out = {};
          for (const s of sectionSlides) if (by[s.id]) out[s.id] = applySlideBlock(contents[s.id] || s.content || {}, by[s.id], s);
          return out;
        },
        strip: stripSlideBlocks,
        onCommitBlock: (id, content) => {
          const s = sectionSlides.find((x) => x.id === id);
          return s ? onSave({ ...s, content }) : null;
        },
      }}
      /* WHERE SHE IS. It rode in `picker` — the slot for a CONTROL — which the
         bar's back now replaces entirely, so it would have gone silent. As
         `where` it is the same line the other five draw, and it gains the slide:
         "Deck · Section · Title slide" writing one, "Deck · Section" writing
         the section. */
      where={`${presentationLabel(deck)} · ${section?.name || "Section"}`}
    />
  );
}
